import clsx from "clsx";
import { isEquipmentBased } from "common";
import React, { MouseEvent, ReactElement } from "react";
import { Event, Permission, Timeslot } from "types";
import { allowGuideAndInventoryAssign } from "../../../models/Event";
import { IsGranted } from "../../Common/IsGranted";
import { EditEventEquipmentModal } from "../EditEventEquipmentModal";
import { EmptyResourceDisplay } from "./EmptyResourceDisplay";
import {
    DashboardResourceType,
    DisplayType,
    ResourceDisplay,
    ResourceDisplayElement,
    ResourceIcon,
} from "./ResourceDisplay";
import { useExperience } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useResource } from "@/hooks/useResource";
import { useModal } from "@/hooks/useVisibility";

interface DashboardInventoryResourceProps {
    icon: ReactElement;
    className?: string;
    timeslot: Timeslot;
    displayType?: DisplayType;
}

const getColor = (needsResourceAssignment?: boolean) => {
    return needsResourceAssignment ? "danger" : "success";
};

export const DashboardInventoryResource = ({
    icon,
    className,
    timeslot,
    displayType = DisplayType.FULL,
}: DashboardInventoryResourceProps) => {
    const { isGranted } = useAuthorization();
    const seller = useSeller();
    const { data: event } = useResource<Event>(["events", timeslot.event?.id]);
    const { experience } = useExperience(timeslot.product);
    const type = "resourceUsages";
    const canAssign = experience ? !isEquipmentBased(experience) && experience.resources?.length > 0 : true;
    const needsResourceAssignment = event?.[type].every((resource) => resource.count === 0) ?? true;

    const shouldShowDot = needsResourceAssignment;
    const {
        isVisible: isVisibleEventEquipmentModal,
        show: showEditEquipmentModal,
        hide: hideEditEquipmentModal,
    } = useModal(false);

    const handleClick = (e: MouseEvent<HTMLSpanElement>) => {
        if (canAssign) {
            e.stopPropagation?.();
            showEditEquipmentModal();
        }
    };

    const isCompact = displayType === DisplayType.COMPACT;
    const isNotAssignable =
        !isGranted(Permission.MODIFY_RESOURCES_EQUIPMENT) || (experience && isEquipmentBased(experience));

    if ((needsResourceAssignment && isNotAssignable) || !allowGuideAndInventoryAssign(seller, event, timeslot)) {
        return <EmptyResourceDisplay isCompact={isCompact} needsResourceAssignment={needsResourceAssignment} />;
    }

    const ResourceContent = (
        <ResourceDisplayElement type={DashboardResourceType.RESOURCE} event={event} isCompact={isCompact} />
    );

    return (
        <>
            <span className={clsx(canAssign && "hover:cursor-pointer", "max-w-fit")} onClick={handleClick}>
                <ResourceDisplay
                    icon={<ResourceIcon event={event} icon={icon} type={DashboardResourceType.RESOURCE} />}
                    color={getColor(needsResourceAssignment)}
                    className={clsx(
                        isCompact ? "inline-block" : "w-24 truncate xl:w-28 2xl:w-32",
                        isCompact && !shouldShowDot && "pl-1",
                        className,
                    )}
                    shouldShowDot={shouldShowDot}
                >
                    {ResourceContent}
                </ResourceDisplay>
            </span>
            {isVisibleEventEquipmentModal && experience ? (
                <IsGranted permissions={Permission.MODIFY_RESOURCES_EQUIPMENT}>
                    <EditEventEquipmentModal
                        eventId={event?.id}
                        isOpen={isVisibleEventEquipmentModal}
                        timeslot={timeslot}
                        experience={experience}
                        onClose={(): void => {
                            hideEditEquipmentModal();
                        }}
                    />
                </IsGranted>
            ) : null}
        </>
    );
};
